import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
  active?: boolean;
}

const base =
  'inline-flex items-center justify-center gap-1.5 font-medium select-none whitespace-nowrap ' +
  'transition-[background-color,color,box-shadow,transform] duration-150 ' +
  'disabled:opacity-40 disabled:pointer-events-none active:scale-[0.97] ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--bg-panel-solid)]';

const variants: Record<Variant, string> = {
  primary:
    'bg-[var(--accent-solid)] text-white shadow-[0_1px_2px_rgba(0,0,0,0.14)] ' +
    'hover:bg-[var(--accent-solid-hover)]',
  secondary:
    'bg-[var(--bg-sunken)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)] ' +
    'shadow-[inset_0_0_0_1px_var(--hairline)]',
  ghost: 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]',
  danger: 'text-[var(--danger)] hover:bg-[color-mix(in_srgb,var(--danger)_12%,transparent)]',
};

const sizes: Record<Size, string> = {
  sm: 'h-7 px-2.5 text-[12px] rounded-[7px]',
  md: 'h-8 px-3 text-[13px] rounded-[8px]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', icon, active, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cn(
        base,
        variants[variant],
        sizes[size],
        active && 'bg-[var(--accent-soft)] text-[var(--accent)]',
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
});

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  active?: boolean;
  tone?: 'default' | 'danger';
  size?: number;
}

export function IconButton({
  label,
  active,
  tone = 'default',
  size = 30,
  className,
  children,
  ...rest
}: IconButtonProps) {
  return (
    <button
      aria-label={label}
      title={label}
      style={{ width: size, height: size }}
      className={cn(
        'inline-flex items-center justify-center rounded-[8px] transition-all duration-150',
        'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]',
        'active:scale-[0.94] disabled:opacity-35 disabled:pointer-events-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]',
        active && 'bg-[var(--accent-soft)] text-[var(--accent)] hover:bg-[var(--accent-soft)]',
        tone === 'danger' && 'hover:text-[var(--danger)]',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
